import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Building2, Briefcase, Plus, X, Settings, Calendar, Key, Mail, Lock,
    LayoutGrid, List, Table, Info, Search, AlertTriangle, ChevronRight, ChevronDown, Trash2, Pencil, Cctv, Loader2
} from 'lucide-react';

const SuperAdminCompanies = () => {
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Modal States
    const [companyModalOpen, setCompanyModalOpen] = useState(false);
    const [projectModalOpen, setProjectModalOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const [createdCreds, setCreatedCreds] = useState(null);

    // Company Form State
    const [companyName, setCompanyName] = useState('');
    const [companyEmail, setCompanyEmail] = useState('');

    // Project Form State
    const [targetCompanyId, setTargetCompanyId] = useState('');
    const [projectName, setProjectName] = useState('');
    const [projectAddress, setProjectAddress] = useState('');
    const [projectContract, setProjectContract] = useState('');
    const [projectLat, setProjectLat] = useState('');
    const [projectLng, setProjectLng] = useState('');
    const [newProjPlanType, setNewProjPlanType] = useState('Pro');
    const [newProjValidUntil, setNewProjValidUntil] = useState('');

    // Edit Project Modal State
    const [editProjectModalOpen, setEditProjectModalOpen] = useState(false);
    const [editingProject, setEditingProject] = useState(null);
    const [editProjectName, setEditProjectName] = useState('');
    const [editProjectAddress, setEditProjectAddress] = useState('');
    const [editProjectContract, setEditProjectContract] = useState('');

    // Views & Filters
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table' | 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedCompanies, setExpandedCompanies] = useState({});

    // Detail Modal State
    const [detailModalOpen, setDetailModalOpen] = useState(false);
    const [selectedProjectDetail, setSelectedProjectDetail] = useState(null);

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
    };

    const fetchOverview = async () => {
        try {
            const response = await axios.get('http://localhost:4000/api/superadmin/overview', { headers });
            setCompanies(response.data);
        } catch (err) {
            console.error("Error loading SuperAdmin overview:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOverview();
    }, []);

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        if (!companyName || !companyEmail) return;
        setFormLoading(true);
        try {
            const res = await axios.post('http://localhost:4000/api/superadmin/companies', {
                name: companyName,
                contact_email: companyEmail
            }, { headers });

            setCompanyName('');
            setCompanyEmail('');
            setCompanyModalOpen(false);
            fetchOverview();
            
            if (res.data.adminCreated) {
                setCreatedCreds({
                    companyName: res.data.company.name,
                    email: res.data.adminUser.email,
                    password: res.data.adminUser.temporaryPassword,
                    emailPreviewUrl: res.data.emailPreviewUrl
                });
            } else {
                alert('Empresa cliente creada con éxito.');
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error al crear la empresa.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!targetCompanyId || !projectName) return;
        setFormLoading(true);
        try {
            const res = await axios.post('http://localhost:4000/api/superadmin/projects', {
                company_id: targetCompanyId,
                name: projectName,
                address: projectAddress,
                contract_number: projectContract,
                location_lat: projectLat ? parseFloat(projectLat) : null,
                location_lng: projectLng ? parseFloat(projectLng) : null,
                plan_type: newProjPlanType,
                valid_until: newProjValidUntil || null
            }, { headers });

            setProjectName('');
            setProjectAddress('');
            setProjectContract('');
            setProjectLat('');
            setProjectLng('');
            setNewProjPlanType('Pro');
            setNewProjValidUntil('');
            setProjectModalOpen(false);
            fetchOverview();
            
            if (res.data.adminCreated) {
                setCreatedCreds({
                    companyName: companies.find(c => c.id === parseInt(targetCompanyId, 10))?.name || 'Nueva Empresa',
                    email: res.data.adminUser.email,
                    password: res.data.adminUser.temporaryPassword,
                    emailPreviewUrl: res.data.emailPreviewUrl
                });
            } else {
                alert(`Proyecto creado con éxito. Licencia asignada: ${newProjPlanType}.`);
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error al crear el proyecto.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleOpenEditProject = (project) => {
        setEditingProject(project);
        setEditProjectName(project.name || '');
        setEditProjectAddress(project.address || '');
        setEditProjectContract(project.contractNumber || '');
        setEditProjectModalOpen(true);
    };

    const handleSaveEditProject = async (e) => {
        e.preventDefault();
        if (!editingProject || !editProjectName.trim()) return;
        setFormLoading(true);
        try {
            await axios.put(`http://localhost:4000/api/superadmin/projects/${editingProject.id}`, {
                name: editProjectName.trim(),
                address: editProjectAddress,
                contract_number: editProjectContract
            }, { headers });
            setEditProjectModalOpen(false);
            setEditingProject(null);
            fetchOverview();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al actualizar el proyecto.');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteProject = async (project) => {
        const confirmName = prompt(`⚠️ ¡ADVERTENCIA LOPD COMPLIANT!\nEsta acción eliminará de forma irreversible el proyecto "${project.name}", sus licencias, cámaras, incidentes EHS, asistencias, bitácoras y toda su información histórica.\n\nPara confirmar, escribe el nombre del proyecto a continuación:`);
        if (confirmName !== project.name) {
            alert('Confirmación incorrecta. Operación cancelada.');
            return;
        }

        setLoading(true);
        try {
            await axios.delete(`http://localhost:4000/api/superadmin/projects/${project.id}`, { headers });
            alert('Proyecto eliminado exitosamente conforme a la LOPD.');
            fetchOverview();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al eliminar el proyecto.');
            setLoading(false);
        }
    };

    const handleDeleteCompany = async (company) => {
        const confirmName = prompt(`🚨 ¡ALERTA DE SEGURIDAD LOPD COMPLIANT!\nEsta acción eliminará la empresa "${company.name}", todos sus proyectos asociados, sus usuarios, licencias, EHS, asistencias, bitácoras y toda su información histórica.\n\nPara confirmar, escribe el nombre de la empresa a continuación:`);
        if (confirmName !== company.name) {
            alert('Confirmación incorrecta. Operación cancelada.');
            return;
        }

        setLoading(true);
        try {
            await axios.delete(`http://localhost:4000/api/superadmin/companies/${company.id}`, { headers });
            alert('Cliente y todos sus datos relacionados eliminados exitosamente conforme a la LOPD.');
            fetchOverview();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al eliminar el cliente.');
            setLoading(false);
        }
    };

    const handleResendAccess = async (company) => {
        const confirmResend = window.confirm(`¿Estás seguro de que deseas regenerar la contraseña y reenviar el correo de acceso a ${company.name}?`);
        if (!confirmResend) return;

        setLoading(true);
        try {
            const res = await axios.post(`http://localhost:4000/api/superadmin/companies/${company.id}/resend-access`, {}, { headers });
            alert(`Correo de acceso reenviado con éxito a ${company.email || company.adminEmail}.`);
            fetchOverview();
            if (res.data.emailPreviewUrl) {
                setCreatedCreds({
                    companyName: company.name,
                    email: company.email || company.adminEmail,
                    password: res.data.tempPassword,
                    emailPreviewUrl: res.data.emailPreviewUrl
                });
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Error al reenviar el correo de acceso.');
        } finally {
            setLoading(false);
        }
    };

    const toggleExpandCompany = (companyId) => {
        setExpandedCompanies(prev => ({
            ...prev,
            [companyId]: !prev[companyId]
        }));
    };

    const formatExpiryDate = (dateStr) => {
        if (!dateStr) return 'Sin vigencia';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return 'Fecha inválida';
            return d.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        } catch (e) {
            return 'Fecha inválida';
        }
    };

    const getRemainingTime = (dateStr, status) => {
        if (!dateStr) return { text: 'Sin vigencia activa', color: 'text-zinc-500' };
        try {
            const expiry = new Date(dateStr);
            if (isNaN(expiry.getTime())) return { text: 'Fecha inválida', color: 'text-zinc-500' };
            
            const now = new Date();
            expiry.setHours(0, 0, 0, 0);
            now.setHours(0, 0, 0, 0);
            
            const diffTime = expiry.getTime() - now.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (status === 'expired' || diffDays < 0) {
                const absoluteDays = Math.abs(diffDays);
                return { 
                    text: `Expiró hace ${absoluteDays} ${absoluteDays === 1 ? 'día' : 'días'}`, 
                    color: 'text-rose-500 font-semibold' 
                };
            } else if (diffDays === 0) {
                return { text: 'Expira hoy', color: 'text-yellow-500 font-bold animate-pulse' };
            } else if (diffDays <= 7) {
                return { text: `${diffDays} ${diffDays === 1 ? 'día' : 'días'} restantes (Vence pronto)`, color: 'text-amber-500 font-semibold' };
            } else {
                return { text: `${diffDays} días restantes`, color: 'text-emerald-450 font-medium' };
            }
        } catch (e) {
            return { text: 'Error de cálculo', color: 'text-zinc-500' };
        }
    };

    const filteredCompanies = companies.map(company => {
        const companyMatches = company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                               (company.email && company.email.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const filteredProjects = company.projects.filter(project => 
            project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (project.contractNumber && project.contractNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (project.address && project.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (project.licenseKey && project.licenseKey.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        if (companyMatches) {
            return company;
        } else if (filteredProjects.length > 0) {
            return { ...company, projects: filteredProjects };
        }
        return null;
    }).filter(Boolean);

    const flatProjectsList = [];
    companies.forEach(company => {
        company.projects.forEach(project => {
            flatProjectsList.push({
                ...project,
                companyId: company.id,
                companyName: company.name,
                companyEmail: company.email
            });
        });
    });

    const filteredFlatProjectsList = flatProjectsList.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.contractNumber && p.contractNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.address && p.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (p.licenseKey && p.licenseKey.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-theme-bg flex items-center justify-center">
                <div className="relative">
                    <div className="w-16 h-16 border-4 border-purple-500/20 rounded-full"></div>
                    <div className="w-16 h-16 border-4 border-purple-500 rounded-full border-t-transparent animate-spin absolute top-0 left-0"></div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-theme-bg text-theme-text-primary p-4 md:p-8 font-sans pb-20">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-theme-border/80 pb-6">
                    <div>
                        <h1 className="text-3xl font-extrabold tracking-tight text-theme-text-primary flex items-center gap-3">
                            <Building2 className="text-purple-500" size={32} /> Clientes y Obras
                        </h1>
                        <p className="text-theme-text-secondary text-xs mt-1">Gestión administrativa de empresas cliente, proyectos de obra y credenciales de acceso LOPD.</p>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => setCompanyModalOpen(true)}
                            className="bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs px-4 py-2.5 rounded-xl shadow-lg shadow-purple-600/15 flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <Plus size={16} /> Agregar Cliente
                        </button>
                        <button
                            onClick={() => {
                                if (companies.length === 0) {
                                    alert("Registra al menos una empresa cliente antes de crear una obra.");
                                    return;
                                }
                                setTargetCompanyId(companies[0].id.toString());
                                setProjectModalOpen(true);
                            }}
                            className="bg-theme-panel hover:bg-theme-border/30 text-theme-text-primary border border-theme-border font-bold text-xs px-4 py-2.5 rounded-xl flex items-center gap-2 transition-all cursor-pointer"
                        >
                            <Plus size={16} /> Crear Obra
                        </button>
                    </div>
                </div>

                {/* Filtros + Switcher */}
                <div className="bg-theme-panel/60 backdrop-blur-xl border border-theme-border rounded-2xl p-4 flex flex-col md:flex-row justify-between items-center gap-4 shadow-md">
                    <div className="relative w-full md:w-96">
                        <Search className="absolute left-3 top-2.5 text-zinc-500" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar cliente, obra, contrato, licencia..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-theme-bg border border-theme-border rounded-xl py-2 pl-10 pr-4 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                        />
                        {searchTerm && (
                            <button onClick={() => setSearchTerm('')} className="absolute right-3 top-2.5 text-zinc-550 hover:text-theme-text-primary">
                                <X size={16} />
                            </button>
                        )}
                    </div>
                    <div className="flex bg-theme-bg border border-theme-border p-1 rounded-xl gap-1 w-full md:w-auto justify-center">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                viewMode === 'grid' ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400' : 'text-zinc-550 hover:text-theme-text-primary'
                            }`}
                        >
                            <LayoutGrid size={16} /> Grid
                        </button>
                        <button
                            onClick={() => setViewMode('table')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                viewMode === 'table' ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400' : 'text-zinc-550 hover:text-theme-text-primary'
                            }`}
                        >
                            <Table size={16} /> Tabla
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                                viewMode === 'list' ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400' : 'text-zinc-550 hover:text-theme-text-primary'
                            }`}
                        >
                            <List size={16} /> Lista
                        </button>
                    </div>
                </div>

                {/* Contenedor Principal de Datos */}
                {filteredCompanies.length === 0 ? (
                    <div className="bg-theme-panel/40 border border-theme-border/80 rounded-2xl p-16 text-center text-theme-text-secondary italic max-w-lg mx-auto">
                        No se encontraron clientes ni obras que coincidan con tu búsqueda.
                    </div>
                ) : viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredCompanies.map(company => (
                            <div key={company.id} className="bg-theme-panel/60 border border-theme-border rounded-3xl p-6 shadow-xl relative overflow-hidden flex flex-col justify-between hover:border-theme-border/80 transition-all duration-300">
                                <div>
                                    <div className="flex justify-between items-start gap-4">
                                        <div className="space-y-1">
                                            <h3 className="text-lg font-bold text-theme-text-primary tracking-tight">{company.name}</h3>
                                            <p className="text-[10px] text-theme-text-secondary font-mono">{company.email || company.adminEmail}</p>
                                        </div>
                                        <button
                                            onClick={() => handleResendAccess(company)}
                                            className="text-theme-text-secondary hover:text-purple-400 p-2 bg-theme-bg border border-theme-border hover:border-purple-500/25 rounded-xl transition-all cursor-pointer"
                                            title="Regenerar contraseña y enviar correo"
                                        >
                                            <Key size={14} />
                                        </button>
                                    </div>

                                    {/* Lista de Proyectos de la Empresa */}
                                    <div className="mt-6 space-y-3.5">
                                        <h4 className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-widest">Obras y Proyectos ({company.projects.length})</h4>
                                        {company.projects.length === 0 ? (
                                            <p className="text-theme-text-secondary italic text-[11px]">Sin obras registradas.</p>
                                        ) : (
                                            company.projects.map(proj => (
                                                <div key={proj.id} className="p-3 bg-theme-bg/60 border border-theme-border/60 rounded-2xl flex justify-between items-center group relative hover:border-theme-border transition-all">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                                                            <span className="font-semibold text-xs text-theme-text-primary">{proj.name}</span>
                                                        </div>
                                                        <p className="text-[10px] text-theme-text-secondary font-mono truncate max-w-[150px]">{proj.address || 'Sin dirección registrada'}</p>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 opacity-60 group-hover:opacity-100 transition-all">
                                                        <button
                                                            onClick={() => {
                                                                setSelectedProjectDetail({ ...proj, companyName: company.name });
                                                                setDetailModalOpen(true);
                                                            }}
                                                            className="text-theme-text-secondary hover:text-theme-text-primary p-1.5 bg-theme-panel rounded-lg cursor-pointer"
                                                            title="Ficha técnica"
                                                        >
                                                            <Info size={12} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleOpenEditProject(proj)}
                                                            className="text-theme-text-secondary hover:text-blue-400 p-1.5 bg-theme-panel rounded-lg cursor-pointer"
                                                            title="Editar obra"
                                                        >
                                                            <Pencil size={12} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProject(proj)}
                                                            className="text-theme-text-secondary hover:text-rose-500 p-1.5 bg-theme-panel rounded-lg cursor-pointer"
                                                            title="Eliminar obra"
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>

                                <div className="mt-6 pt-4 border-t border-theme-border flex justify-between items-center text-[10px] text-theme-text-secondary">
                                    <span>ID: #{company.id}</span>
                                    <button 
                                        onClick={() => handleDeleteCompany(company)}
                                        className="text-rose-500/70 hover:text-rose-400 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                                    >
                                        <Trash2 size={12} /> Eliminar Cliente
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : viewMode === 'table' ? (
                    <div className="bg-theme-panel border border-theme-border rounded-3xl overflow-hidden shadow-2xl">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-theme-border bg-theme-bg/40 text-[10px] font-black uppercase tracking-wider text-theme-text-secondary">
                                        <th className="p-4 pl-6">Obra / Proyecto</th>
                                        <th className="p-4">Cliente (Empresa)</th>
                                        <th className="p-4">Contrato</th>
                                        <th className="p-4">Dirección</th>
                                        <th className="p-4">Cámaras</th>
                                        <th className="p-4">Licencia</th>
                                        <th className="p-4 pr-6 text-right">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-theme-border/60 text-xs text-theme-text-primary">
                                    {filteredFlatProjectsList.map(proj => (
                                        <tr key={proj.id} className="hover:bg-theme-border/20 transition-all">
                                            <td className="p-4 pl-6 font-bold text-theme-text-primary">{proj.name}</td>
                                            <td className="p-4 font-semibold text-theme-text-secondary">{proj.companyName}</td>
                                            <td className="p-4 font-mono text-[11px]">{proj.contractNumber || '—'}</td>
                                            <td className="p-4 text-theme-text-secondary max-w-[200px] truncate">{proj.address || '—'}</td>
                                            <td className="p-4"><span className="px-2 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-md font-mono text-[11px] font-bold">{proj.totalCameras}</span></td>
                                            <td className="p-4">
                                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                                    proj.licenseStatus === 'active' ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' : 'bg-rose-500/10 border border-rose-500/20 text-rose-450'
                                                }`}>
                                                    {proj.planType}
                                                </span>
                                            </td>
                                            <td className="p-4 pr-6 text-right space-x-1">
                                                <button
                                                    onClick={() => {
                                                        setSelectedProjectDetail(proj);
                                                        setDetailModalOpen(true);
                                                    }}
                                                    className="p-1.5 hover:bg-theme-bg text-theme-text-secondary hover:text-theme-text-primary rounded-lg transition-all cursor-pointer"
                                                    title="Ficha técnica"
                                                >
                                                    <Info size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleOpenEditProject(proj)}
                                                    className="p-1.5 hover:bg-theme-bg text-theme-text-secondary hover:text-blue-400 rounded-lg transition-all cursor-pointer"
                                                    title="Editar obra"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteProject(proj)}
                                                    className="p-1.5 hover:bg-theme-bg text-theme-text-secondary hover:text-rose-500 rounded-lg transition-all cursor-pointer"
                                                    title="Eliminar obra"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {filteredCompanies.map(company => (
                            <div key={company.id} className="bg-theme-panel border border-theme-border rounded-2xl overflow-hidden shadow-lg transition-all">
                                <div 
                                    onClick={() => toggleExpandCompany(company.id)}
                                    className="p-4 bg-theme-bg/20 flex justify-between items-center cursor-pointer hover:bg-theme-bg/40 transition-all select-none"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center">
                                            <Building2 size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-sm text-theme-text-primary">{company.name}</h3>
                                            <p className="text-[10px] text-theme-text-secondary font-mono">{company.email || company.adminEmail}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-[10px] px-2 py-0.5 bg-theme-bg border border-theme-border rounded-full font-mono text-theme-text-secondary">{company.projects.length} obras</span>
                                        {expandedCompanies[company.id] ? <ChevronDown className="text-theme-text-secondary" size={18} /> : <ChevronRight className="text-theme-text-secondary" size={18} />}
                                    </div>
                                </div>
                                
                                {expandedCompanies[company.id] && (
                                    <div className="p-4 border-t border-theme-border bg-theme-bg/10 divide-y divide-theme-border/50">
                                        {company.projects.length === 0 ? (
                                            <div className="py-4 text-center text-theme-text-secondary italic text-xs">Sin obras o proyectos registrados.</div>
                                        ) : (
                                            company.projects.map(proj => (
                                                <div key={proj.id} className="py-3 flex justify-between items-center">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <Briefcase size={12} className="text-purple-400" />
                                                            <span className="font-bold text-xs text-theme-text-primary">{proj.name}</span>
                                                        </div>
                                                        <p className="text-[10px] text-theme-text-secondary max-w-[300px] truncate">{proj.address || 'Sin dirección registrada'}</p>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <span className={`text-[10px] font-bold ${
                                                            proj.licenseStatus === 'active' ? 'text-emerald-450' : 'text-rose-450'
                                                        }`}>{proj.planType} ({proj.licenseStatus === 'active' ? 'Activo' : 'Vencida'})</span>
                                                        <div className="flex items-center gap-1 border-l border-theme-border pl-3">
                                                            <button
                                                                onClick={() => {
                                                                    setSelectedProjectDetail({ ...proj, companyName: company.name });
                                                                    setDetailModalOpen(true);
                                                                }}
                                                                className="p-1 hover:text-theme-text-primary text-theme-text-secondary rounded cursor-pointer"
                                                                title="Ficha técnica"
                                                            >
                                                                <Info size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleOpenEditProject(proj)}
                                                                className="p-1 hover:text-blue-450 text-theme-text-secondary rounded cursor-pointer"
                                                                title="Editar obra"
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteProject(proj)}
                                                                className="p-1 hover:text-rose-450 text-theme-text-secondary rounded cursor-pointer"
                                                                title="Eliminar obra"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* MODAL: REGISTRAR EMPRESA CLIENTE */}
            {companyModalOpen && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-6 relative">
                        <button onClick={() => setCompanyModalOpen(false)} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Building2 className="text-purple-400" size={22} /> Registrar Nuevo Cliente
                            </h3>
                            <p className="text-xs text-theme-text-secondary">Creará una nueva cuenta de empresa y generará credenciales administradoras.</p>
                        </div>
                        <form onSubmit={handleCreateCompany} className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Nombre de la Empresa</label>
                                <input
                                    type="text"
                                    required
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    placeholder="Ej. Construtora Hola Telecom"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Correo Electrónico de Contacto</label>
                                <input
                                    type="email"
                                    required
                                    value={companyEmail}
                                    onChange={(e) => setCompanyEmail(e.target.value)}
                                    placeholder="Ej. contacto@empresa.com"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={formLoading}
                                className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-theme-bg text-white font-bold p-3 rounded-xl text-xs flex justify-center items-center gap-2 shadow-lg shadow-purple-600/10 cursor-pointer"
                            >
                                {formLoading ? <Loader2 className="animate-spin" size={16} /> : 'Registrar Empresa Cliente'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: REGISTRAR PROYECTO / OBRA */}
            {projectModalOpen && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-lg w-full p-6 shadow-2xl space-y-6 relative max-h-[90vh] overflow-y-auto">
                        <button onClick={() => setProjectModalOpen(false)} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Briefcase className="text-purple-400" size={22} /> Crear Nueva Obra / Proyecto
                            </h3>
                            <p className="text-xs text-theme-text-secondary">Asocia un nuevo frente de trabajo a una empresa cliente y genera su clave de licencia.</p>
                        </div>
                        <form onSubmit={handleCreateProject} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Empresa Cliente</label>
                                    <select
                                        value={targetCompanyId}
                                        onChange={(e) => setTargetCompanyId(e.target.value)}
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    >
                                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Nombre del Proyecto</label>
                                    <input
                                        type="text"
                                        required
                                        value={projectName}
                                        onChange={(e) => setProjectName(e.target.value)}
                                        placeholder="Ej. Torre Chapultepec"
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Dirección Física de la Obra</label>
                                <input
                                    type="text"
                                    value={projectAddress}
                                    onChange={(e) => setProjectAddress(e.target.value)}
                                    placeholder="Calle, Número, Ciudad, Estado"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Número de Contrato</label>
                                    <input
                                        type="text"
                                        value={projectContract}
                                        onChange={(e) => setProjectContract(e.target.value)}
                                        placeholder="Ej. CONTR-2026-X"
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Latitud</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={projectLat}
                                        onChange={(e) => setProjectLat(e.target.value)}
                                        placeholder="Ej. 19.4326"
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Longitud</label>
                                    <input
                                        type="number"
                                        step="any"
                                        value={projectLng}
                                        onChange={(e) => setProjectLng(e.target.value)}
                                        placeholder="Ej. -99.1332"
                                        className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                    />
                                </div>
                            </div>

                            <div className="border-t border-theme-border/80 pt-4 space-y-4">
                                <h4 className="text-xs font-bold text-theme-text-primary flex items-center gap-1.5">
                                    <Key size={14} className="text-purple-400" /> Configuración de Licencia Inicial
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Plan Contratado</label>
                                        <select
                                            value={newProjPlanType}
                                            onChange={(e) => setNewProjPlanType(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                        >
                                            <option value="Básico">Básico (1 Cámara + Visualización)</option>
                                            <option value="Pro">Pro (Hasta 4 Cámaras + EHS básico)</option>
                                            <option value="Enterprise">Enterprise (Cámaras ilimitadas + Todo EHS e IA)</option>
                                        </select>
                                    </div>
                                    <div className="space-y-1.5">
                                        <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Fecha de Vencimiento</label>
                                        <input
                                            type="date"
                                            required
                                            value={newProjValidUntil}
                                            onChange={(e) => setNewProjValidUntil(e.target.value)}
                                            className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-purple-500/50"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={formLoading}
                                className="w-full bg-purple-600 hover:bg-purple-500 disabled:bg-theme-bg text-white font-bold p-3 rounded-xl text-xs flex justify-center items-center gap-2 shadow-lg shadow-purple-600/10 cursor-pointer"
                            >
                                {formLoading ? <Loader2 className="animate-spin" size={16} /> : 'Crear Obra y Generar Licencia'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: EDITAR PROYECTO / OBRA */}
            {editProjectModalOpen && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-6 relative animate-fade-in">
                        <button onClick={() => { setEditProjectModalOpen(false); setEditingProject(null); }} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1">
                            <h3 className="text-xl font-bold text-theme-text-primary flex items-center gap-2">
                                <Pencil className="text-blue-400" size={22} /> Editar Información de Obra
                            </h3>
                            <p className="text-xs text-theme-text-secondary">Modifica los detalles generales del frente de trabajo.</p>
                        </div>
                        <form onSubmit={handleSaveEditProject} className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Nombre del Proyecto</label>
                                <input
                                    type="text"
                                    required
                                    value={editProjectName}
                                    onChange={(e) => setEditProjectName(e.target.value)}
                                    placeholder="Nombre del proyecto"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Dirección Física</label>
                                <input
                                    type="text"
                                    value={editProjectAddress}
                                    onChange={(e) => setEditProjectAddress(e.target.value)}
                                    placeholder="Ubicación física de la obra"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black uppercase text-theme-text-secondary tracking-wider">Número de Contrato</label>
                                <input
                                    type="text"
                                    value={editProjectContract}
                                    onChange={(e) => setEditProjectContract(e.target.value)}
                                    placeholder="Número de contrato"
                                    className="w-full bg-theme-bg border border-theme-border rounded-xl p-3 text-sm text-theme-text-primary focus:outline-none focus:border-blue-500/50"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={formLoading}
                                className="w-full bg-blue-600 hover:bg-blue-550 disabled:bg-theme-bg text-white font-bold p-3 rounded-xl text-xs flex justify-center items-center gap-2 shadow-lg cursor-pointer"
                            >
                                {formLoading ? <Loader2 className="animate-spin" size={16} /> : 'Guardar Cambios'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL: FICHA TÉCNICA Y DETALLES */}
            {detailModalOpen && selectedProjectDetail && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border border-theme-border rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-6 relative max-h-[85vh] overflow-y-auto">
                        <button onClick={() => { setDetailModalOpen(false); setSelectedProjectDetail(null); }} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1">
                            <span className="text-[9px] px-2 py-0.5 bg-purple-500/10 border border-purple-500/25 text-purple-400 rounded-full font-bold uppercase tracking-wider inline-block">
                                Ficha Técnica Obra
                            </span>
                            <h3 className="text-xl font-bold text-theme-text-primary tracking-tight">{selectedProjectDetail.name}</h3>
                            <p className="text-[10px] text-theme-text-secondary font-semibold uppercase">Cliente: {selectedProjectDetail.companyName}</p>
                        </div>
                        <div className="space-y-4 text-xs">
                            <div className="bg-theme-bg border border-theme-border/80 rounded-2xl p-4 space-y-2">
                                <div className="flex justify-between border-b border-theme-border/40 pb-2">
                                    <span className="text-theme-text-secondary font-medium">Contrato</span>
                                    <span className="font-mono text-theme-text-primary font-bold">{selectedProjectDetail.contractNumber || '—'}</span>
                                </div>
                                <div className="flex justify-between border-b border-theme-border/40 pb-2">
                                    <span className="text-theme-text-secondary font-medium">Dirección</span>
                                    <span className="text-theme-text-primary font-semibold max-w-[200px] text-right truncate">{selectedProjectDetail.address || '—'}</span>
                                </div>
                                <div className="flex justify-between pt-1">
                                    <span className="text-theme-text-secondary font-medium">Coordenadas</span>
                                    <span className="text-theme-text-primary font-mono">
                                        {selectedProjectDetail.lat && selectedProjectDetail.lng ? `${selectedProjectDetail.lat}, ${selectedProjectDetail.lng}` : 'Sin geo-tags'}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Detalles de Licencia */}
                            <div className="bg-theme-bg border border-theme-border/80 rounded-2xl p-4 space-y-3">
                                <h4 className="text-[10px] font-bold text-theme-text-secondary uppercase tracking-wider flex items-center gap-1">
                                    <Key size={12} className="text-purple-400" /> Licencia y Suscripción
                                </h4>
                                <div className="space-y-2">
                                    <div className="flex justify-between">
                                        <span className="text-theme-text-secondary font-medium">Plan Activo</span>
                                        <span className="px-2 py-0.5 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-md font-bold text-[10px] uppercase">
                                            {selectedProjectDetail.planType}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-theme-text-secondary font-medium">Clave de Licencia</span>
                                        <span className="font-mono text-theme-text-primary font-semibold">{selectedProjectDetail.licenseKey || '—'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-theme-text-secondary font-medium">Vence el</span>
                                        <span className="text-theme-text-primary font-semibold">{formatExpiryDate(selectedProjectDetail.validUntil)}</span>
                                    </div>
                                    <div className="flex justify-between pt-1 border-t border-theme-border">
                                        <span className="text-theme-text-secondary font-medium">Vigencia</span>
                                        <span className={getRemainingTime(selectedProjectDetail.validUntil, selectedProjectDetail.licenseStatus).color}>
                                            {getRemainingTime(selectedProjectDetail.validUntil, selectedProjectDetail.licenseStatus).text}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Detalle de Cámaras */}
                            <div className="bg-theme-bg border border-theme-border/80 rounded-2xl p-4 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <Cctv size={16} className="text-purple-400" />
                                    <span className="text-theme-text-secondary font-medium">Cámaras IoT Conectadas</span>
                                </div>
                                <span className="px-2.5 py-0.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full font-mono text-[11px] font-bold">
                                    {selectedProjectDetail.totalCameras} {selectedProjectDetail.totalCameras === 1 ? 'dispositivo' : 'dispositivos'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* AVISO / COMPONENTED CREDENCIALES GENERADAS */}
            {createdCreds && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-theme-panel border-2 border-purple-500/30 rounded-3xl max-w-md w-full p-6 shadow-2xl space-y-6 relative">
                        <button onClick={() => setCreatedCreds(null)} className="absolute right-4 top-4 text-theme-text-secondary hover:text-theme-text-primary cursor-pointer">
                            <X size={20} />
                        </button>
                        <div className="space-y-1.5 text-center">
                            <div className="w-12 h-12 bg-purple-500/10 rounded-full flex items-center justify-center text-purple-400 mx-auto border border-purple-500/20">
                                <Mail size={22} />
                            </div>
                            <h3 className="text-lg font-bold text-theme-text-primary tracking-tight">¡Credenciales Generadas!</h3>
                            <p className="text-xs text-theme-text-secondary">Se ha creado la cuenta de administración y se envió el correo con accesos.</p>
                        </div>
                        <div className="bg-theme-bg border border-theme-border rounded-2xl p-4 space-y-3.5 text-xs">
                            <div className="flex justify-between border-b border-theme-border/40 pb-2">
                                <span className="text-theme-text-secondary font-medium">Empresa</span>
                                <span className="text-theme-text-primary font-bold">{createdCreds.companyName}</span>
                            </div>
                            <div className="flex justify-between border-b border-theme-border/40 pb-2">
                                <span className="text-theme-text-secondary font-medium">Usuario (Email)</span>
                                <span className="text-theme-text-primary font-mono font-semibold">{createdCreds.email}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-theme-text-secondary font-medium">Contraseña Temporal</span>
                                <span className="bg-purple-500/10 border border-purple-500/25 px-2.5 py-1 text-purple-400 rounded-lg font-mono font-black tracking-wider text-sm select-all">
                                    {createdCreds.password}
                                </span>
                            </div>
                        </div>

                        {createdCreds.emailPreviewUrl && (
                            <div className="bg-theme-bg border border-theme-border rounded-xl p-3 text-[10px] text-theme-text-secondary flex items-center justify-between gap-3">
                                <span>📧 Simulación de Mailer disponible para pruebas.</span>
                                <a 
                                    href={createdCreds.emailPreviewUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer" 
                                    className="text-purple-400 hover:text-purple-300 font-bold underline shrink-0"
                                >
                                    Ver Correo
                                </a>
                            </div>
                        )}

                        <div className="bg-amber-500/5 border border-amber-500/20 p-3 rounded-2xl flex items-start gap-3">
                            <AlertTriangle className="text-amber-500 shrink-0 mt-0.5" size={16} />
                            <p className="text-[10px] text-amber-500/80 leading-normal">
                                **Importante**: Por motivos de seguridad (LOPD), esta contraseña solo se mostrará una vez. Por favor cópiala o indícale al administrador que cambie su contraseña temporal tras su primer ingreso.
                            </p>
                        </div>

                        <button 
                            onClick={() => setCreatedCreds(null)}
                            className="w-full bg-theme-bg hover:bg-theme-border/30 text-theme-text-primary font-bold p-3 rounded-xl text-xs transition-all border border-theme-border cursor-pointer"
                        >
                            He Copiado las Credenciales
                        </button>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SuperAdminCompanies;
